import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Sparkles, Users, Sprout, ShieldCheck,
  TrendingUp, Heart, Star, Quote, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  heroImages, koperasiInfo, testimonials, news, formatRupiah, formatNumber
} from "@/lib/dummyData";
import { PublicAPI, ProductAPI } from "@/lib/api";
import { useEffect, useState } from "react";

// Konten default homepage (fallback bila backend belum termuat).
const defaultHp = {
  hero_badge: "RAT 2026 · 15 Maret · daftar sekarang",
  hero_title: "Ekonomi gotong-royong untuk era digital.",
  hero_highlight: "gotong-royong",
  hero_subtitle: `Bergabung dengan ${formatNumber(koperasiInfo.members)} anggota yang menabung, berbelanja, dan menumbuhkan SHU bersama. Transparan, mudah, dan benar-benar milik Anda.`,
  cta_primary: "Daftar Jadi Anggota",
  cta_secondary: "Lihat Toko",
  stats: [
    { label: "Anggota Aktif", value: String(koperasiInfo.members), suffix: "" },
    { label: "Desa Terjangkau", value: String(koperasiInfo.villages), suffix: "" },
    { label: "Cabang", value: String(koperasiInfo.branches), suffix: "" },
    { label: "SHU 2024", value: "1.24", suffix: " M" },
  ],
  promo_label: "Estimasi SHU",
  promo_value: "Rp 1.24M",
  promo_note: "Pembagian Maret 2026",
  features_heading: "Layanan yang jujur, dibangun oleh anggota — untuk anggota.",
};

// Sisipkan <em> pada kata highlight di dalam judul.
function renderTitle(title, highlight) {
  if (!highlight || !title.includes(highlight)) return title;
  const [pre, ...rest] = title.split(highlight);
  return (<>{pre}<span className="font-editorial italic font-medium text-forest">{highlight}</span>{rest.join(highlight)}</>);
}

const features = [
  { icon: Sprout, title: "Simpan & Tumbuh", desc: "Simpanan Pokok, Wajib, dan Sukarela dengan SHU transparan yang dihitung otomatis tiap kuartal.", color: "bg-forest text-white" },
  { icon: ShieldCheck, title: "Aman & Diawasi", desc: "Diawasi langsung oleh Dinas Koperasi DIY, audit eksternal tahunan, dan kepatuhan UU PDP.", color: "bg-amber-warm text-ink" },
  { icon: TrendingUp, title: "SHU Akurat", desc: "Pelacak SHU real-time berdasarkan jasa modal dan jasa usaha — tanpa misteri.", color: "bg-sage text-ink" },
  { icon: Heart, title: "Untuk Anggota", desc: "Harga khusus anggota, cicilan ringan, dan akses pinjaman modal usaha hingga Rp 50 juta.", color: "bg-terracotta text-white" },
];

export default function Home() {
  // Konten landing page dari CMS (Setting Homepage), fallback ke default.
  const [hp, setHp] = useState(defaultHp);
  useEffect(() => {
    PublicAPI.homepage().then((d) => { if (d) setHp({ ...defaultHp, ...d }); }).catch(() => {});
  }, []);
  const stats = hp.stats || defaultHp.stats;

  // Produk unggulan = 4 produk terbaru dari katalog asli (bagian disembunyikan bila kosong).
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    ProductAPI.list().then((rows) => setFeatured((rows || []).slice(0, 4).map((p) => ({
      id: p.id, name: p.name, category: p.category, image: p.image,
      price: Number(p.price_non_member), memberPrice: Number(p.price_member),
    })))).catch(() => {});
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-10 lg:pt-20 pb-14 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-stone-soft text-xs uppercase tracking-[0.18em] text-ink/75 mb-6">
                <Sparkles size={13} className="text-amber-warm" /> {hp.hero_badge}
              </div>
              <h1 className="font-display text-[42px] leading-[1.02] sm:text-6xl lg:text-7xl font-extrabold text-ink tracking-tight">
                {renderTitle(hp.hero_title, hp.hero_highlight)}
              </h1>
              <p className="mt-6 max-w-xl text-base lg:text-lg text-ink/75 leading-relaxed">
                {hp.hero_subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/daftar">
                  <Button className="rounded-full bg-forest hover:bg-forest-dark text-white h-12 px-6 text-sm" data-testid="hero-cta-daftar">
                    {hp.cta_primary} <ArrowUpRight size={17} />
                  </Button>
                </Link>
                <Link to="/toko">
                  <Button variant="outline" className="rounded-full h-12 px-6 text-sm border-ink/15 hover:bg-white" data-testid="hero-cta-toko">
                    {hp.cta_secondary} <ArrowRight size={17} />
                  </Button>
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                {stats.map((s, i) => (
                  <div key={s.label} className={`animate-fade-up delay-${(i + 1) * 100}`}>
                    <div className="font-display text-3xl lg:text-4xl font-bold text-ink">
                      {Number(s.value) < 100 ? s.value : formatNumber(Number(s.value))}<span className="text-amber-warm">{s.suffix}</span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.16em] text-ink/55 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] overflow-hidden border border-stone-soft shadow-xl bg-stone-soft/30 aspect-[4/5]">
                <img src={heroImages.primary} alt="Komunitas koperasi" className="w-full h-full object-cover" />
                <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/95 backdrop-blur p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-forest text-white grid place-items-center"><Users size={20} /></div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-ink/55">Anggota minggu ini</div>
                    <div className="font-display font-bold text-ink">+84 keluarga bergabung</div>
                  </div>
                  <ChevronRight className="text-forest" />
                </div>
              </div>
              <div className="absolute -top-6 -left-6 hidden lg:block">
                <div className="rounded-2xl bg-amber-warm text-ink p-4 shadow-lg max-w-[180px] rotate-[-4deg]">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">{hp.promo_label}</div>
                  <div className="font-display text-2xl font-bold">{hp.promo_value}</div>
                  <div className="text-xs">{hp.promo_note}</div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 hidden lg:block">
                <div className="rounded-2xl bg-white border border-stone-soft p-4 shadow-lg flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[12, 23, 33].map((id) => <img key={id} src={`https://i.pravatar.cc/60?img=${id}`} alt="" className="w-8 h-8 rounded-full border-2 border-white" />)}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-ink">+4.8k anggota aktif</div>
                    <div className="text-[10px] text-ink/55">terverifikasi resmi</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* leaf divider */}
        <div className="leaf-divider h-6 opacity-60"></div>
      </section>

      {/* FEATURE BENTO */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="pill-tag bg-forest/10 text-forest mb-4">Kenapa Koperasi Andalan Dapur Nusantara</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-ink tracking-tight">
              {renderTitle(hp.features_heading, "jujur")}
            </h2>
          </div>
          <p className="text-ink/75 text-base max-w-md">
            Tidak ada bunga predatori, tidak ada "syarat & ketentuan" tersembunyi. Setiap rupiah
            dapat dilacak, dan setiap keputusan dibawa ke Rapat Anggota Tahunan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <div key={f.title} className={`rounded-2xl border border-stone-soft bg-white p-6 hover-lift animate-fade-up delay-${(i + 1) * 100}`} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center mb-4 ${f.color}`}>
                <f.icon size={22} />
              </div>
              <h3 className="font-display text-lg font-semibold text-ink mb-2">{f.title}</h3>
              <p className="text-sm text-ink/75 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUK UNGGULAN */}
      {featured.length > 0 && (
      <section className="bg-white border-y border-stone-soft py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
            <div>
              <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-4">Toko Koperasi</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Produk Unggulan dari Anggota</h2>
            </div>
            <Link to="/toko" className="text-forest font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
              Lihat semua produk <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.map((p, i) => (
              <Link key={`feat-${p.id}`} to={`/toko/${p.id}`} className={`group rounded-2xl border border-stone-soft bg-white hover-lift overflow-hidden animate-fade-up delay-${(i + 1) * 100}`} data-testid={`featured-product-${p.id}`}>
                <div className="aspect-square overflow-hidden bg-cream">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink/55">{p.category}</div>
                  <div className="font-medium text-ink text-sm mt-1 line-clamp-2 min-h-[2.4rem]">{p.name}</div>
                  <div className="flex items-baseline gap-2 mt-3">
                    <div className="font-display font-bold text-ink">{formatRupiah(p.price)}</div>
                    <div className="text-[11px] text-amber-700 font-semibold">/anggota {formatRupiah(p.memberPrice)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONI */}
      <section className="py-16 lg:py-24 overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 mb-10">
          <div className="pill-tag bg-sage/20 text-forest mb-4">Cerita Anggota</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight max-w-3xl">
            "Bukan sekadar tempat menabung — ini <span className="font-editorial italic">rumah ekonomi</span> kami."
          </h2>
        </div>

        <div className="relative">
          <div className="flex gap-5 marquee-track w-max">
            {[...testimonials, ...testimonials].map((t, i) => (
              <article key={i} className="w-[360px] shrink-0 rounded-2xl bg-white border border-stone-soft p-6">
                <Quote className="text-amber-warm" size={28} />
                <p className="text-sm text-ink mt-3 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 pt-4 border-t border-stone-soft/60 flex items-center justify-between">
                  <div>
                    <div className="font-display font-semibold text-sm text-ink">{t.name}</div>
                    <div className="text-[11px] text-ink/55 uppercase tracking-wider">Anggota Aktif</div>
                  </div>
                  <div className="flex text-amber-warm gap-0.5">
                    {Array.from({ length: t.rating }).map((_, idx) => <Star key={idx} size={14} fill="currentColor" />)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BERITA */}
      <section className="bg-cream py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="flex justify-between items-end mb-10 gap-4">
            <div>
              <div className="pill-tag bg-terracotta/10 text-terracotta mb-4">Berita & Pengumuman</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Yang terbaru dari koperasi</h2>
            </div>
            <Link to="/berita" className="text-forest font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
              Semua berita <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {news.slice(0, 3).map((n, i) => (
              <article key={n.id} className={`group rounded-2xl bg-white border border-stone-soft overflow-hidden hover-lift animate-fade-up delay-${(i + 1) * 100}`} data-testid={`news-card-${n.id}`}>
                <div className="aspect-[16/10] overflow-hidden bg-stone-soft/30">
                  <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <Badge className="bg-sage/20 text-forest hover:bg-sage/30 rounded-full border-0 text-[10px] uppercase tracking-wider">{n.category}</Badge>
                  <h3 className="font-display font-semibold text-ink mt-3 text-lg leading-snug line-clamp-2 group-hover:text-forest transition">{n.title}</h3>
                  <p className="text-sm text-ink/75 mt-2 line-clamp-2">{n.excerpt}</p>
                  <div className="text-xs text-ink/55 mt-4">{new Date(n.date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="relative rounded-[2rem] bg-forest text-cream overflow-hidden grain-overlay blob-bg hover-lift animate-fade-up">
            <div className="relative z-10 p-8 lg:p-10">
              <div className="pill-tag bg-amber-warm/20 text-amber-warm mb-4">Untuk Anggota Baru</div>
              <h3 className="font-display text-3xl lg:text-4xl font-bold leading-tight">
                Mulai menabung mulai <span className="font-editorial italic text-amber-warm">Rp 25.000</span>.
              </h3>
              <p className="mt-3 text-cream/80 text-sm">
                Pendaftaran 100% online, verifikasi 1×24 jam. Dapatkan kartu anggota digital,
                akses toko harga khusus, dan SHU tahunan.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/daftar">
                  <Button className="bg-amber-warm hover:bg-amber-warm/90 text-ink rounded-full h-11 px-6 font-semibold" data-testid="cta-banner-daftar">
                    Daftar Sekarang <ArrowUpRight size={16} />
                  </Button>
                </Link>
                <Link to="/profil">
                  <Button variant="outline" className="rounded-full h-11 px-6 border-cream/30 text-cream hover:bg-white/10 hover:text-cream bg-transparent">
                    Pelajari
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="relative rounded-[2rem] bg-amber-warm text-ink overflow-hidden grain-overlay blob-bg hover-lift animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative z-10 p-8 lg:p-10">
              <div className="pill-tag bg-ink/10 text-ink mb-4">Untuk Anggota Aktif</div>
              <h3 className="font-display text-3xl lg:text-4xl font-bold leading-tight">
                Punya produk? <span className="font-editorial italic">Jual bareng</span> di toko koperasi.
              </h3>
              <p className="mt-3 text-ink/75 text-sm">
                Anggota dapat membuka lapak sendiri — kelola produk, stok, pesanan, dan pendapatan
                langsung di Ruang Anggota. Komisi nol untuk 100 transaksi pertama.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/anggota/jualan">
                  <Button className="bg-ink hover:bg-ink/90 text-cream rounded-full h-11 px-6 font-semibold" data-testid="cta-banner-jualan">
                    Mulai Jualan <ArrowUpRight size={16} />
                  </Button>
                </Link>
                <Link to="/lacak">
                  <Button variant="outline" className="rounded-full h-11 px-6 border-ink/30 text-ink hover:bg-ink/10 bg-transparent">
                    Lacak Pesanan Tamu
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
